import * as cssDefs from 'business/businessHeaderCssDefs';
import { ACTION_SELECT_BUSINESS_LIST_RESULT } from 'utils/analytics/eventTypeConstantsAmplitude';
import { CATEGORY_CITYWIDE_SEARCH } from 'utils/analytics/eventTypesConstants';
import { FunctionComponent } from 'react';
import { LegacyPlaceholderBusinessImage, getBusinessThumbnailSrc } from '@tock/images';
import { Typography } from 'omakase';
import { consumer } from '@tock/coretypes';
import { ga4SendEvent } from 'components/consent-restricted-scripts/GoogleAnalytics';
import { getBusinessSubtitle } from 'utils/businessUtils';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useSearchHandlerContext } from 'components/search/SearchContext';
import { useTheme } from '@emotion/react';
import Anchor from 'components/Anchor';
import DisplayDistance from '@tock/shared/components/number/DisplayDistance';
import ThumbImage from 'components/images/ThumbImage';
import clsx from 'clsx';

interface Props {
  business: consumer.OfferingBusiness;
  distanceMeters?: number;
  goBackTo?: boolean;
  className?: string;
  locale?: string;
  index?: number;
}

const BusinessHeader: FunctionComponent<Props> = ({
  business,
  className,
  distanceMeters,
  goBackTo,
  locale,
  index,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const { analyticsSearchContext } = { ...useSearchHandlerContext() };
  const theme = useTheme();
  const imgSrc =
    getBusinessThumbnailSrc(business, { height: 146, width: 260 }) ||
    LegacyPlaceholderBusinessImage;
  const subtitle = getBusinessSubtitle(business, {
    excludes: { locality: true },
  });
  const handleClick = () => {
    const { name } = business;
    window.analytics.sendEvent(
      CATEGORY_CITYWIDE_SEARCH,
      'Citywide search result business clicked',
      name,
      index || null
    );
    const data = {
      businessName: name,
      businessListIndex: index,
      searchContext: analyticsSearchContext,
    };
    amplitudeSendEvent(ACTION_SELECT_BUSINESS_LIST_RESULT, data);
    ga4SendEvent('select_business_list_result', data);
  };

  return (
    <Anchor
      href={`/${
        business.domainName
      }?tock_source=tock&tock_medium=availability_search&tock_content=restaurant_box${
        goBackTo ? `&goBackTo=1` : ''
      }`}
      onClick={handleClick}
      css={cssDefs.link}
    >
      <section css={cssDefs.root} className={clsx('BusinessHeader', className)}>
        <section css={cssDefs.content}>
          <Typography
            component="h2"
            variant="heading04"
            color="fgDefault"
            mb={0.5}
            noWrap
            data-testid="search-business-name"
          >
            {business.name}
          </Typography>
          <Typography color="fgDefault" variant="body02" noWrap>
            {distanceMeters ? (
              <>
                <DisplayDistance locale={locale} meters={distanceMeters} /> {' · '}
              </>
            ) : null}
            {subtitle}
          </Typography>
        </section>
        <div css={cssDefs.thumbContainer}>
          <ThumbImage
            imgSrc={imgSrc}
            dominantColor={theme.colors.borderDefault}
            ariaLabel={business.name}
          />
        </div>
      </section>
    </Anchor>
  );
};

export default BusinessHeader;
